import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef,PLATFORM_ID,inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TipoTicket, Precio, TicketsService } from '../../core/services/tickets.service';
import { CartService, CartTicketItem } from '../../core/services/cart.service';
import { GuestSession } from '../../core/services/guest-session';
import { Subscription } from 'rxjs';
import { AppSyncService } from '../../core/services/app-sync.service';
import { API_CONFIG } from '../../core/config/api.config';
import { Event } from '../../core/services/events.service';
import Swal from 'sweetalert2';

interface TicketSelection {
  tipoticketid: string;
  precioid: string;
  cantidad: number;
  precio: Precio;
  tipoTicket: TipoTicket;
}

@Component({
  selector: 'app-ticket-selector',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ticket-selector.html',
  styleUrl: './ticket-selector.css',
})
export class TicketSelector implements OnInit, OnChanges {
  @Input() eventoid?: string;
  @Input() url_slug?: string;
  @Input() event?: Event;
  /** Si se proporcionan, se usan directamente en lugar de hacer llamada a la API */
  @Input() preloadedTipoTickets?: TipoTicket[];
  /** Si se proporciona, carga desde la API pero solo muestra este tipoticketid */
  @Input() filterByTipoTicketId?: string;
  @Output() ticketsAdded = new EventEmitter<void>();

  private platformId = inject(PLATFORM_ID);
  private isBrowser: boolean;

  tiposTicket: TipoTicket[] = [];
  isLoading = true;
  error = false;
  private initialized = false;

  // Mapa para rastrear las cantidades seleccionadas
  selectedQuantities: Map<string, number> = new Map();
  isAddingToCart = false;

  private disponibilidadSubscription?: Subscription;

  constructor(
    private ticketsService: TicketsService,
    private cartService: CartService,
    private guestSession: GuestSession,
    private cdr: ChangeDetectorRef,
    private appSyncService: AppSyncService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.initialized = true;
    console.log('TicketSelector ngOnInit - eventoid:', this.eventoid, 'preloadedTipoTickets:', this.preloadedTipoTickets?.length, 'filterByTipoTicketId:', this.filterByTipoTicketId);
    if (this.preloadedTipoTickets && this.preloadedTipoTickets.length > 0) {
      this.tiposTicket = this.preloadedTipoTickets;
      this.isLoading = false;
    } else if (this.filterByTipoTicketId) {
      this.loadSingleTipoTicket(this.filterByTipoTicketId);
    } else if (this.eventoid) {
      this.loadTiposTicket();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Sync preloadedTipoTickets when input changes after init
    if (changes['preloadedTipoTickets'] && this.initialized) {
      const preloaded = changes['preloadedTipoTickets'].currentValue as TipoTicket[] | undefined;
      if (preloaded && preloaded.length > 0) {
        this.tiposTicket = preloaded;
        this.isLoading = false;
      }
    }
    // Only reload on eventoid change AFTER init (first load is handled by ngOnInit)
    if (changes['eventoid'] && changes['eventoid'].currentValue && this.initialized) {
      this.loadTiposTicket();
    }
  }

  loadSingleTipoTicket(tipoticketid: string): void {
    this.isLoading = true;
    this.error = false;
    this.ticketsService.getTipoTicketByIdForWeb(tipoticketid).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.tiposTicket = [response.data as any];
        } else {
          this.tiposTicket = [];
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
    if (this.url_slug && this.isBrowser) {
      this.setupDisponibilidadSubscription(this.url_slug);
    }
  }
  ngOnDestroy(): void {
    // Cerrar suscripción de disponibilidad
    if (this.disponibilidadSubscription) {
      this.disponibilidadSubscription.unsubscribe();
    }
    this.appSyncService.unsubscribeFromDisponibilidad();
  }

  private setupDisponibilidadSubscription(urlSlug: string): void {
    console.log("Iniciando el appsync");
    // Solo ejecutar en el navegador, no en SSR
    if (!this.isBrowser) {
      console.log('⏭️ Suscripción de disponibilidad omitida (SSR)');
      return;
    }

    try {
      // Suscribirse al observable para recibir actualizaciones de disponibilidad
      this.disponibilidadSubscription = this.appSyncService.disponibilidadChanged$.subscribe({
        next: (data) => {
          console.log('📡 Actualización de disponibilidad recibida:', data);
        /*
        {
    "tipoticketid": "918c040a-a7e6-430b-9a3a-400b67271f99",
    "eventoid": "f5b3d101-005d-4eab-963c-8cf4611c22dc",
    "url_slug": "demos-paul",
    "nombre_localidad": "General",
    "aforo_maximo": 100,
    "aforo_disponible": 100,
    "tickets_reservados": 0,
    "estado": "ACTIVO",
    "timestamp": "2026-01-10T22:49:54.592Z"
}
        */
        // Actualizar la disponibilidad del tipo de ticket correspondiente
        const tipoTicket = this.tiposTicket.find(t => t.tipoticketid === data.tipoticketid);
        if (tipoTicket) {
          tipoTicket.aforo_disponible = data.aforo_disponible;
          tipoTicket.tickets_reservados = data.tickets_reservados;
          tipoTicket.aforo_maximo = data.aforo_maximo;
          console.log('✅ Tipo de ticket actualizado:', tipoTicket);
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error en suscripción de disponibilidad:', error);
      }
      });

      // Iniciar la suscripción GraphQL
      this.appSyncService.subscribeToDisponibilidad(urlSlug);
    } catch (error) {
      console.error('❌ Error al configurar suscripción de disponibilidad:', error);
    }
  }

  loadTiposTicket(): void {
    if (!this.eventoid) {
      console.log('TicketSelector: No eventoid provided');
      return;
    }

    console.log('TicketSelector: Loading tipos ticket for event:', this.eventoid);
    this.isLoading = true;
    this.error = false;

    this.ticketsService.getTiposTicketByEvento(this.eventoid).subscribe({
      next: (response) => {
        console.log('TicketSelector: API response:', response);
        if (response.success) {
          // Verificar si el evento tiene el canal web publicado y disponible
          const canalWeb = this.event?.canales?.find(c => c.canal === 'web');
          const permitirCanalWeb = canalWeb?.publicado === true && canalWeb?.disponible === true;
          
          console.log('TicketSelector: Canal web permitido:', permitirCanalWeb, canalWeb);
          
          // Filtrar solo los tipos de ticket activos y que tengan precios
          // El estado viene en minúsculas desde la API: 'activo' no 'ACTIVO'
          this.tiposTicket = response.data
            .filter(tipo => (tipo.estado || '').toLowerCase() === 'activo' && tipo.precios.length > 0)
            .map(tipo => ({
              ...tipo,
              // Filtrar precios según el canal permitido
              precios: tipo.precios.filter(precio => {
                // Si el canal web está permitido, solo mostrar precios web publicados y disponibles
                if (permitirCanalWeb && precio.canal === 'web') {
                  return precio.publicado === true && precio.disponible === true;
                }
                // Si el canal web NO está permitido, no mostrar ningún precio web
                // (esto permite agregar lógica futura para otros canales)
                return false;
              })
            }))
            // Eliminar tipos de ticket que se quedaron sin precios después del filtro
            .filter(tipo => tipo.precios.length > 0);
            
          console.log('TicketSelector: Filtered tipos ticket:', this.tiposTicket);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('TicketSelector: Error loading tipos ticket:', error);
        this.error = true;
        this.isLoading = false;
      }
    });
    
    // Iniciar suscripción de disponibilidad en tiempo real solo si tenemos url_slug y estamos en el navegador
    if (this.url_slug && this.isBrowser) {
      this.setupDisponibilidadSubscription(this.url_slug);
    } else if (!this.url_slug) {
      console.warn('⚠️ No url_slug disponible para suscripción de disponibilidad');
    }
  }

  getMaxTickets(aforoDisponible: number): number {
    const limit = this.event?.max_tickets_por_compra ?? 10;
    return this.ticketsService.getMaxTickets(aforoDisponible, limit);
  }

  getQuantityOptions(aforoDisponible: number): number[] {
    const max = this.getMaxTickets(aforoDisponible);
    return this.ticketsService.getQuantityOptions(max);
  }

  getSelectedQuantity(tipoticketid: string, precioid: string): number {
    const key = `${tipoticketid}-${precioid}`;
    return this.selectedQuantities.get(key) || 0;
  }

  getPrecioId(precio: Precio | null | undefined): string {
    return this.ticketsService.getPrecioId(precio);
  }

  setSelectedQuantity(tipoticketid: string, precioid: string, cantidad: number): void {
    const key = `${tipoticketid}-${precioid}`;
    if (cantidad === 0) {
      this.selectedQuantities.delete(key);
    } else {
      this.selectedQuantities.set(key, cantidad);
    }
  }

  getTotalSelected(): number {
    let total = 0;
    this.selectedQuantities.forEach(cantidad => {
      total += cantidad;
    });
    return total;
  }

  getTotalPrice(): number {
    let total = 0;
    this.tiposTicket.forEach(tipo => {
      tipo.precios.forEach(precio => {
        const cantidad = this.getSelectedQuantity(tipo.tipoticketid, this.getPrecioId(precio));
        total += cantidad * precio.precio_total;
      });
    });
    return total;
  }

  addToCart(): void {
    if (this.isAddingToCart) return;

    // Construir array de tickets para la API
    const tickets: CartTicketItem[] = [];

    this.tiposTicket.forEach(tipo => {
      tipo.precios.forEach(precio => {
        const cantidad = this.getSelectedQuantity(tipo.tipoticketid, this.getPrecioId(precio));
        if (cantidad > 0) {
          tickets.push({
            tipoticketid: tipo.tipoticketid,
            quantity: cantidad,
            precioId: this.getPrecioId(precio)
          });
        }
      });
    });

    if (tickets.length === 0) {
      console.warn('No tickets selected');
      return;
    }

    this.isAddingToCart = true;

    // Obtener session ID (null en modo cookie_http_only)
    const sessionId = this.guestSession.getSessionId();
    
    console.log('🔍 [DEBUG] Session ID:', sessionId || '(cookie mode)');
    console.log('🔍 [DEBUG] cookie_http_only activo:', API_CONFIG.cookie_http_only);
    console.log('🔍 [DEBUG] Session activa:', this.guestSession.isSessionActive());
    
    if (this.isBrowser) {
      // Mostrar todas las cookies (solo las no-HttpOnly serán visibles)
      console.log('🔍 [DEBUG] Cookies visibles:', document.cookie);
      
      if (API_CONFIG.cookie_http_only) {
        console.log('🔍 [DEBUG] Modo cookie HttpOnly: la cookie guest_session_id no es visible desde JavaScript');
        console.log('🔍 [DEBUG] El backend validará automáticamente la cookie HTTP-only');
      }
    }
    
    // En modo cookie, sessionId será null (correcto)
    // En modo manual, verificar que exista sessionId
    if (!API_CONFIG.cookie_http_only && !sessionId) {
      console.error('No session ID available');
      this.isAddingToCart = false;
      return;
    }
    
    // Verificar que la sesión esté activa (aplica a ambos modos)
    if (!this.guestSession.isSessionActive()) {
      console.log('🔐 Creando nueva sesión...');
      this.guestSession.ensureSession().subscribe({
        next: (response) => {
          if (response.success) {
            // Reintentar agregar al carrito
            this.addToCart();
          }
        },
        error: (error) => {
          console.error('Error creating session:', error);
          this.isAddingToCart = false;
        }
      });
      return;
    }

    // Construir request (sessionId será null en modo cookie)
    const request = {
      sessionId,
      tickets
    };

    console.log('Adding to cart:', request);

    // Verificar límite max_tickets_por_compra contra tickets ya en carrito
    const maxPorCompra = this.event?.max_tickets_por_compra ?? 10;
    const totalSeleccionado = this.getTotalSelected();

    this.cartService.getCartTicketCount(sessionId).subscribe({
      next: (ticketsEnCarrito) => {
        const totalConCarrito = ticketsEnCarrito + totalSeleccionado;
        if (totalConCarrito > maxPorCompra) {
          const disponibles = maxPorCompra - ticketsEnCarrito;
          Swal.fire({
            icon: 'warning',
            title: 'Límite de tickets alcanzado',
            html: `Solo puedes comprar <strong>${maxPorCompra}</strong> tickets por compra. Ya tienes <strong>${ticketsEnCarrito}</strong> en tu carrito. Puedes agregar hasta <strong>${disponibles > 0 ? disponibles : 0}</strong> más.`,
            confirmButtonColor: '#e8343f',
          });
          this.isAddingToCart = false;
          return;
        }

        // Verificar si el carrito ya tiene tickets de otro evento
        this.cartService.checkCartEventConflict(sessionId, this.eventoid!).subscribe({
          next: async (conflict) => {
            if (conflict) {
              const result = await Swal.fire({
                icon: 'warning',
                title: 'Carrito con otro evento',
                html: `No puedes agregar tickets de otro evento. Actualmente tienes tickets de <strong>"${conflict.eventName}"</strong> en tu carrito. Debes completar o eliminar ese carrito primero.`,
                showCancelButton: true,
                confirmButtonText: 'Eliminar carrito',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#e8343f',
                cancelButtonColor: '#6b7280',
                reverseButtons: true
              });
              if (result.isConfirmed) {
                this.cartService.clearCart(conflict.cartid).subscribe({
                  next: () => {
                    this.cartService.notifyTicketsAdded();
                    this.isAddingToCart = false;
                    this.cdr.detectChanges();
                  },
                  error: (err) => {
                    console.error('Error clearing cart:', err);
                    this.isAddingToCart = false;
                    this.cdr.detectChanges();
                  }
                });
              } else {
                this.isAddingToCart = false;
                this.cdr.detectChanges();
              }
              return;
            }
            this.executeAddToCart(request);
          },
          error: () => this.executeAddToCart(request)
        });
      },
      error: () => {
        // Si falla obtener el carrito, intentar agregar de todas formas
        this.cartService.checkCartEventConflict(sessionId, this.eventoid!).subscribe({
          next: async (conflict) => {
            if (conflict) {
              this.isAddingToCart = false;
              return;
            }
            this.executeAddToCart(request);
          },
          error: () => this.executeAddToCart(request)
        });
      }
    });
  }

  private executeAddToCart(request: { sessionId: string | null; tickets: CartTicketItem[] }): void {
    // Llamar a la API
    this.cartService.addTicketsByQuantity(request).subscribe({
      next: (response) => {
        console.log('Cart response:', response);
        if (response.success) {
          // Notificar globalmente que se agregaron tickets
          this.cartService.notifyTicketsAdded();
          // Emitir evento para notificar que se agregaron tickets
          this.ticketsAdded.emit();
          // Limpiar selecciones
          this.selectedQuantities.clear();
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'No se pudieron agregar los tickets',
            text: response.message || 'Ocurrió un error inesperado',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#e8343f'
          });
        }
        this.isAddingToCart = false;
      },
      error: (error) => {
        console.error('Error adding to cart:', error);
        const message = error?.error?.message || error?.message || 'Error al agregar tickets al carrito';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: message,
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#e8343f'
        });
        this.isAddingToCart = false;
      }
    });
  }

  hasSelectedTickets(): boolean {
    return this.getTotalSelected() > 0;
  }
}
